import { Box, CheckBox, Form, FormField, Text } from 'grommet';
import { Hint } from '..';
import { CreateSelect, TalonInput } from '../..';
import { Advertiser } from '../../../../../lib/api';

interface IStartTabProps {
  name: string;
  code: string;
  client?: Partial<Advertiser>;
  product: string;
  budget: number;
  clientList: Partial<Advertiser>[];
  setName: (value: string) => void;
  setCode: (value: string) => void;
  setClient: (value?: Partial<Advertiser>) => void;
  setClientList: (value: Partial<Advertiser>[]) => void;
  setProduct: (value: string) => void;
  setBudget: (value: number) => void;
}

export const StartTab = ({
  name,
  code,
  client,
  product,
  budget,
  clientList,
  setName,
  setCode,
  setClient,
  setClientList,
  setProduct,
  setBudget,
}: IStartTabProps) => {
  /** Methods **/

  const selectClient = (clientName: string) => {
    if (!clientName) return;
    const selectedClient = clientList.find(
      (client) => client.name?.toLocaleLowerCase() === clientName.toLocaleLowerCase()
    );
    if (selectedClient) {
      setClient(selectedClient);
    } else {
      const client = { id: 'NEW', name: clientName, isSagSignatory: false, isJpcAuthorizer: false };
      setClientList([...clientList, client]);
      setClient(client);
    }
  };

  /** Render **/
  return (
    <Box pad="medium" direction="column" width="100%" gap="small">
      <Box>
        <Text>
          Welcome! Let's get started creating a new project. Fill in as much as you can, and don't worry if you don't
          know some answers right now. You can always make changes later. (This is the only tab in the wizard with
          required* fields.)
        </Text>
      </Box>
      <Box direction="row">
        <Box direction="column">
          <Form validate="blur" id="start">
            <Box direction="row">
              <TalonInput
                id="project-name"
                label="Project Name"
                name="project-name"
                required
                val={name}
                onChange={(name) => setName(name)}
                validate={[(val: string) => (val.length > 3 ? true : undefined)]}
                aria-required
              />
            </Box>
            <Box direction="row">
              <TalonInput
                id="job-code"
                label="Job Code"
                name="job-code"
                required
                val={code}
                onChange={(code) => setCode(code)}
                aria-required
              />
            </Box>
            <Box direction="row" pad={{ vertical: 'medium' }}>
              <FormField label="Client" htmlFor="select-client" name="client" required>
                <CreateSelect
                  id="select-client"
                  defaultOptions={clientList.map((client) => client.name || '')}
                  onChange={selectClient}
                  defaultValue={client?.name}
                  required
                  placeholder="Select Client"
                  name="client"
                />
              </FormField>
              <Box direction="column" gap="small" width="medium" pad={{ left: 'medium' }}>
                <Text>Client is a:</Text>
                <CheckBox
                  label="SAG Signatory"
                  checked={client?.isSagSignatory}
                  onChange={(e) => setClient({ ...client, isSagSignatory: e.target.checked })}
                  disabled={!client || client.id !== 'NEW'}
                />
                <CheckBox
                  label="JPC Authorizer"
                  checked={client?.isJpcAuthorizer}
                  onChange={(e) => setClient({ ...client, isJpcAuthorizer: e.target.checked })}
                  disabled={!client || client.id !== 'NEW'}
                />
              </Box>
            </Box>
            <Box direction="row">
              <TalonInput
                id="product"
                label="Product"
                name="product"
                required
                val={product}
                onChange={(product) => setProduct(product)}
                aria-required
              />
            </Box>
            <Box direction="column" gap="small" width="medium">
              <TalonInput
                id="budget"
                label="Talent Budget"
                name="budget"
                val={budget}
                onChange={(budget) => setBudget(budget)}
                aria-required
                format="currency"
                currencyDecimals={false}
              />
            </Box>
          </Form>
        </Box>
        <Box width="50%">
          {budget > 1000000 ? (
            <Hint>
              <Text>Heads up! This budget is over $1,000,000, so the Low Budget Waiver will not apply.</Text>
            </Hint>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
